import * as Yup from "yup";
import Swal from "sweetalert2";

/**
 * Validates form values against a Yup schema and displays user-friendly errors
 * @param {Yup.ObjectSchema} schema - The Yup validation schema
 * @param {Object} values - The form values to validate
 * @returns {Promise<boolean>} Boolean indicating if validation passed
 */
const getOrdinal = (index) => {
  const ordinals = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'];
  return ordinals[index] || `${index + 1}th`;
};

const prettifyPath = (path) => {
  if (!path) return 'Field';

  const parts = path.split(/[\[\].]/).filter(Boolean);
  let result = '';
  let currentContext = '';

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    const nextPart = parts[i + 1];

    if (!isNaN(part)) {
      const index = parseInt(part, 10);
      const ordinal = getOrdinal(index);
      const section = parts[i - 1];

      switch (section) {
        case 'tenants':
          result = `${ordinal} Tenant`;
          break;
        case 'occupants':
          result = `${ordinal} Occupant`;
          break;
        case 'pets':
          result = `${ordinal} Pet`;
          break;
        case 'parking':
          result = `${ordinal} Parking Slot`;
          break;
        case 'storage':
          result = `${ordinal} Storage Unit`;
          break;
        case 'attached_documents':
          result = `${ordinal} Document`;
          break;
        case 'tenantSignatures':
          result = `${ordinal} Tenant Signature`;
          break;
        default:
          result = `${ordinal} Item`;
      }

      if (nextPart) {
        const fieldName = nextPart
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());
        result += ` ${fieldName}`;
      }
    } else {
      if (part === 'lease_details' || part === 'property_details' || part === 'security_deposits') {
        currentContext = part.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        continue;
      } else if (part === 'manager' || part === 'telus' || part === 'pro_ration' || part === 'pet_fees' || part === 'allowed_pets' || part === 'appliances') {
        currentContext = part.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        if (!nextPart || isNaN(nextPart)) {
          const fieldName = nextPart
            ? nextPart.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
            : part.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
          result = currentContext ? `${currentContext} ${fieldName}` : fieldName;
        }
        continue;
      } else if (!result) {
        result = part.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      }
    }
  }

  return result || 'Field';
};

const groupErrors = (errors) => {
  const grouped = {
    PropertyDetails: [],
    Manager: [],
    Tenants: [],
    Occupants: [],
    LeaseDetails: [],
    SecurityDeposits: [],
    AttachedDocuments: [],
    Other: [],
  };

  errors.forEach(({ path, message }) => {
    const label = prettifyPath(path);
    const errorItem = { label, message };

    if (path.startsWith('property_details')) {
      grouped.PropertyDetails.push(errorItem);
    } else if (path.startsWith('manager')) {
      grouped.Manager.push(errorItem);
    } else if (path.startsWith('tenants')) {
      grouped.Tenants.push(errorItem);
    } else if (path.startsWith('occupants')) {
      grouped.Occupants.push(errorItem);
    } else if (path.startsWith('lease_details')) {
      grouped.LeaseDetails.push(errorItem);
    } else if (path.startsWith('security_deposits')) {
      grouped.SecurityDeposits.push(errorItem);
    } else if (path.startsWith('attached_documents')) {
      grouped.AttachedDocuments.push(errorItem);
    } else {
      grouped.Other.push(errorItem);
    }
  });

  return grouped;
};


export const validateStep = async (schema, values) => {
  try {
    await schema.validate(values, { abortEarly: false, context: values });
    return true;
  } catch (err) {
    const errors = err.inner?.map((e) => ({
      path: e.path || '',
      message: e.message,
    })) || [];

    const groupedErrors = groupErrors(errors);

    let html = '<p style="margin-bottom: 10px;">Please correct the following errors:</p>';
    html += `
  <div style="
    text-align: left;
    padding: 20px;
    background-color: #fff3cd;
    border: 1px solid #ffeeba;
    border-radius: 8px;
    font-size: 14px;
  ">
`;

    Object.entries(groupedErrors).forEach(([section, errorList]) => {
      if (errorList.length > 0) {
        const sectionTitle = section.replace(/([A-Z])/g, ' $1').trim();
        html += `<div style="margin-top: 10px;">
      <h3 style="
        font-size: 16px;
        font-weight: bold;
        margin: 10px 0 5px;
        color: #856404;
      ">${sectionTitle}</h3>`;

        errorList.forEach(({ label, message }, idx) => {
          html += `<div style="margin-bottom: 8px; color: #000;">
    <span style="text-decoration: underline; color: #000; margin-right: 4px;">${idx + 1}.) ${label}</span>: ${message}
  </div>`;
        });



        html += '</div>';
      }
    });


    html += '</div>';


    if (errors.length > 0) {
      await Swal.fire({
        icon: 'error',
        title: '🚫 Validation Error',
        html,
        width: 900,
        confirmButtonText: 'Okay',
        customClass: {
          container: 'validation-error-modal',
          title: 'validation-error-title',
          htmlContainer: 'validation-error-content',
          confirmButton: 'validation-error-button',
        },
      });
    }

    return false;
  }
};

/**
 * Formats validation errors for display in an Alert component
 * @param {Object} errors - The validation error object
 * @returns {string[]} An array of formatted error messages
 */
export const formatValidationErrors = (errors) => {
  return Object.entries(errors).map(([field, message]) => {
    const prettyField = prettifyPath(field);
    return `${prettyField}: ${message}`;
  });
};
